<template>
  <section class="ppt-create">
    <UploadZone
      ref="uploadZoneRef"
      :multiple="false"
      :disabled="currentFile?.uid || successFile?.fileUid || (isMobile && !user?.user_id)"
      class="ppt-create__upload"
      :current-preview="fileUrl"
      :max-size="MAX_FILE_SIZE"
      @before-upload="handleBefore"
      @file-select="handleFileSelect"
      @error="handleError"
      :msg="{
        type: '',
        size: `上传文件大小不超过${MAX_FILE_SIZE / 1024 / 1024}MB`,
      }"
      :accept="accept.join(',')"
    >
      <template #default>
        <div class="ppt-create__upload-empty" v-if="!currentFile && !successFile">
          <div class="ppt-create__upload-empty-title">
            <Text color="t1" variant="16">点击或拖拽文件到此上传</Text>
            <span class="ppt-create__upload-empty-title-free">免费试用</span>
          </div>
          <UploadPPT class="ppt-create__upload-empty-icon" />
          <Text color="t7" variant="12"
            >支持格式：PPT、PPTX、PDF，最大{{ MAX_FILE_SIZE / 1024 / 1024 }}MB</Text
          >
        </div>
        <div class="ppt-create__upload-progress" v-else>
          <div class="ppt-create__upload-progress-info">
            <IconPPT class="ppt-create__upload-progress-icon" />
            <div class="ppt-create__upload-progress-name-wrap">
              <Tooltip :content="uploadFileName" content-class="ppt-create__tooltip--page-center">
                <Text color="t1" variant="16" as="span" class="ppt-create__upload-progress-name">
                  {{ uploadFileName }}
                </Text>
              </Tooltip>
            </div>
          </div>
          <div class="ppt-create__upload-progress-desc">
            <LoadingFill class="ppt-create__upload-progress-icon-spin" />
            <Text color="t5" variant="16"> 解析中 </Text>
          </div>
        </div>
      </template>
    </UploadZone>
    <Text color="t3" variant="16" class="ppt-create__hint">
      若没有PPT，可点击使用<a class="ppt-create__link" @click="goToChatPPT">有言AIPPT功能</a>生成PPT
    </Text>
  </section>
</template>
<script setup lang="ts">
import { RESOLUTION } from '@/constants';
import { SESSION_FILE } from '@/constants/config';
import { createWorkWithPreset } from '@/features/createWork';
import { useAccountLogin } from '@/pages/login/shared/lib/useAccountLogin';
import { message } from '@rendora/common/composables/message';
import { GENERIC_ACTION_KEYS } from '@rendora/common/entities/app_cfg';
import type { IMedia } from '@rendora/common/entities/upload_media_v3';
import { showBuyEquityModal, showBuyStorageModal } from '@rendora/common/features/payment';
import DataSource from '@rendora/common/features/upload/oss-upload/data-source';
import { EntityUploadFileItem, useUploadStore } from '@rendora/common/features/upload/store';
import UploadZone from '@rendora/common/features/upload/ui/UploadZone.vue';
import { getI18nService } from '@rendora/common/services/i18n';
import { useUserStore } from '@rendora/common/store';
import { usePermissionStore } from '@rendora/common/store/permission';
import { IRightsType, MEDIA_TYPE, MODEL_TYPE, RESOURCE_TYPE } from '@rendora/common/types/enums';
import { UPLOAD_EVENTS, type UploadEventData } from '@rendora/common/utils/emitter';
import UploadPPT from '@rendora/design/assets/color/upload-ppt.svg?component';
import Link from '@rendora/design/components/link/Link.vue';
import { Text } from '@rendora/design/components/text/index';
import Tooltip from '@rendora/design/components/tooltip/Tooltip.vue';
import { clear, del, get, getMany, set } from 'idb-keyval';
import { last } from 'lodash-es';
import mime from 'mime';
import { storeToRefs } from 'pinia';
import IconPPT from '~/assets/svg/icon-ppt.svg?component';
import LoadingFill from '~/assets/svg/loading-fill.svg';

// 文件类型的定义
export type FileSupportedTypes = 'presentation' | 'pdf' | 'file';
const { startAllUploads, addFiles, removeFile, clearQueue } = useUploadStore();
const uploadStore = useUploadStore();
const { uploadQueue } = storeToRefs(useUploadStore());
const DB = { set, get, del, getMany, clear };
const uploadZoneRef = ref();
const uploadFileName = ref();
const fileUrl = ref<string>('');
const { t: $t } = getI18nService();
const accept = ['.ppt', '.pdf', '.pptx'];
const { show } = useAccountLogin();
const { isMobile } = useDevice();

// 用户信息
const { user } = storeToRefs(useUserStore());

// 生成随机数
const randomFiveDigits = () => Math.floor(Math.random() * 90000) + 10000;
const MAX_FILE_SIZE = 20 * 1024 * 1024;

const currentFile = computed(() => uploadQueue.value?.[0]);
const successFile = ref();
// 检查存储空间
const checkStorageLimit = () => {
  const i18nService = getI18nService();
  const $t = i18nService.t;
  const { user } = useUserStore();
  const { hasActionPermission } = usePermissionStore();

  // 上传前判断是否存储空间已满
  const isOverStorage = () => {
    if (!user?.rights.total_used_storage || !user?.rights.total_storage) return false;
    return user?.rights.total_used_storage >= user?.rights.total_storage;
  };

  if (isOverStorage()) {
    let text = $t('fu-fei-sheng-ji');
    let handler = showBuyEquityModal;
    const allow = hasActionPermission(GENERIC_ACTION_KEYS.PACKAGE_UPGRADE);

    switch (user?.rights_type) {
      case IRightsType.TASTE:
        handler = () => showBuyEquityModal({ targetType: IRightsType.PERSONAL });
        text = $t('fu-fei-sheng-ji');
        break;
      case IRightsType.TEAM_TASTE:
        handler = () => showBuyEquityModal({ targetType: IRightsType.ORG_STANDARD });
        text = $t('fu-fei-sheng-ji');
        break;
      default:
        handler = () => showBuyStorageModal();
        text = $t('jia-gou');
        break;
    }

    const link = h(
      Link,
      {
        style: { color: 'var(--x-purple-6)' },
        onClick: handler,
      },
      text,
    );

    const content = h('div', { class: 'upload-modal-storage-warning' }, [
      $t('yun-cun-chu-kong-jian-yi-man-qing-sheng-ji-quan-yi-hou-cao-zuo'),
      allow ? link : undefined,
    ]);
    return message.warning({
      content,
    });
  }

  return null;
};

const handleError = ({ fileUid, error }: UploadEventData[UPLOAD_EVENTS.ERROR]) => {
  message.error(error);
  clearQueue();
  removeFile(fileUid);
};

const handleUploadComplete = ({ fileUid }: UploadEventData[UPLOAD_EVENTS.COMPLETE]) => {
  const file = uploadStore.getFile(fileUid) as EntityUploadFileItem | undefined;
  if (!file) return;
  successFile.value = file;
  createWork(file);
};

// 注册事件监听器
uploadStore.emitter.on(UPLOAD_EVENTS.COMPLETE, handleUploadComplete);
uploadStore.emitter.on(UPLOAD_EVENTS.ERROR, handleError);

// 组件卸载时清理事件监听器
onUnmounted(() => {
  uploadStore.emitter.off(UPLOAD_EVENTS.COMPLETE, handleUploadComplete);
  uploadStore.emitter.off(UPLOAD_EVENTS.ERROR, handleError);
});

const mediaFilter = (media: IMedia, source_media_type: MEDIA_TYPE, pptTemplateId?: number) => {
  if (media.media_type === MEDIA_TYPE.VIDEO) {
    return {
      media_id: media?.media_id || media.id,
      media_url: media.url,
      cover_image_url: media.cover_image_url,
      media_volume: 100,
      duration_format: media.duration_format,
      media_name: media.name,
      text: media?.metadata?.note ?? '',
      source_media_type,
      media_type: [MEDIA_TYPE.PPT, MEDIA_TYPE.PDF].includes(source_media_type)
        ? media.media_type
        : MEDIA_TYPE.VIDEO,

      // @ts-ignore
      category: media.segment_text,

      // @ts-ignore
      ppt_template_id: media.segment_text ? pptTemplateId : undefined,
    };
  }
  return {
    media_id: media?.media_id || media.id,
    media_url: media.url,
    media_name: media.name,
    text: media?.metadata?.note ?? '',
    source_media_type,
    segment_node_id: media?.segment_node_id,
    render_media_url: media?.render_media_url,
    media_type: [MEDIA_TYPE.PPT, MEDIA_TYPE.PDF].includes(source_media_type)
      ? media.media_type
      : '',
    // AI PPT

    // @ts-ignore
    category: media.category,

    // @ts-ignore
    ppt_template_id: pptTemplateId,
  };
};

const createWork = (file: EntityUploadFileItem) => {
  const { media } = file;
  const segments = [];
  // 处理成编辑器需要的数据格式
  if (media) {
    if ([MEDIA_TYPE.PPT, MEDIA_TYPE.PDF].includes(media.media_type)) {
      if (media.children.length) {
        media.children.forEach((m: any) =>
          segments.push(mediaFilter(m, media.media_type, m.ppt_template_id) as any),
        );
      }
    } else {
      segments.push(mediaFilter(media, media.media_type) as any);
    }
  }
  segments.forEach(seg => (seg.id = randomFiveDigits()));

  createWorkWithPreset({
    create_data: {
      resolution: RESOLUTION.VERTICAL,
      entrance: 1,
    },
    youyanTempNewData: {
      segments,
      need_init: true,
    },
  });
};

// 检查文件类型是否被允许（增强版，处理跨平台 MIME 类型差异）
const checkFileType = (file: File): boolean => {
  const extFromFileName = last(file.name.split('.'));
  const extFromType = last(file.type.split('/'));
  const checkTypeName = (extFromFileName || extFromType || '').toLowerCase();
  return accept.includes(`.${checkTypeName}`);
};

// 获取文件类型的类型
const getFileTypeName = (file: File): FileSupportedTypes => {
  const mimeType = file.type || mime.getType(file.name) || '';
  if (mimeType.includes('presentation')) return 'presentation';
  if (mimeType === 'application/pdf') return 'pdf';
  return 'file';
};

// 检查文件大小是否符合权限限制
// const checkFileSize = (file: File): boolean => {
//   const fileSizeInMB = file.size;
//   const limits = MAX_FILE_SIZE;
//   // 根据文件类型检查大小限制
//   return fileSizeInMB <= limits;
// };

const goToChatPPT = () => {
  if (!user.value?.user_id) {
    navigateTo(`/login?redirect=${encodeURIComponent(encodeURIComponent(location.href))}`);
    return;
  }
  location.href = '/editor/page/ppt';
};

const handleBefore = () => {
  if (!user.value?.user_id && isMobile) {
    navigateTo(`/login?redirect=${encodeURIComponent(encodeURIComponent(location.href))}`);
    return;
  }
};

onMounted(async () => {
  // handleBefore();
  if (user.value?.user_id) {
    const files = await DB.get(SESSION_FILE);
    if (files) {
      handleClickUpload(files);
    }
  }
});

const handleClickUpload = async (files: File) => {
  handleFileSelect(files);
  await DB.del(SESSION_FILE);
};

// 处理文件选择
const handleFileSelect = async (file: File) => {
  if (!user.value?.user_id) {
    await DB.del(SESSION_FILE);
    await DB.set(SESSION_FILE, file);
    show();
    return;
  }

  successFile.value = null;

  clearQueue();

  // 清空错误信息
  const validFiles: File[] = [];

  if (!checkFileType(file)) {
    message.error($t('filename-bu-zhi-chi-de-wen-jian-lei-xing', [file.name]));
    return;
  }

  validFiles.push(file);

  // 检查存储空间限制
  const storageCheckResult = checkStorageLimit();
  if (storageCheckResult) {
    return storageCheckResult;
  }

  // 如果没有有效文件，不进行上传
  if (validFiles.length === 0) {
    uploadZoneRef.value?.clearFiles();
    return;
  }

  addFiles(
    [file],
    {
      dataSource: DataSource,
      auth: {
        user_id: user.value?.user_id ?? '',
      },
      params: {
        upload_type: UPLOAD_TYPE.PERSON,
        resource_type: RESOURCE_TYPE.MATERIAL,
        model_type: MODEL_TYPE.MEDIA,
      },
      is_transcoding: ['video', 'presentation'].includes(getFileTypeName(file)),
    },
    {},
  );
  uploadFileName.value = file?.name || '';

  // 立即开始上传
  startAllUploads();
};
</script>

<style lang="scss" scoped>
.ppt-create {
  margin: 0 auto;
  width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;
}
.ppt-create__hint {
  margin-top: 2px;
}
.ppt-create__link {
  position: relative;
  font-weight: var(--x-weight-medium);
  color: var(--x-purple-6);
  &:hover,
  &:active {
    font-weight: var(--x-weight-medium);
  }
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    width: 0;
    height: 1px;
    background: var(--x-purple-6);
    transform: translateX(-50%);
    transition: width 0.25s ease;
  }
  &:hover::after {
    width: 100%;
  }
}
.ppt-create__upload {
  margin-top: 3px;
  width: 1192px;
  max-width: 100%;
  height: 240px;
  min-height: 240px !important;
  outline: 4px solid var(--x-back-4);
  border: 1px solid var(--x-back-7);
  background: var(--x-back-1) !important;
  &:hover {
    box-shadow: var(--x-shadow);
    border-color: var(--x-back-7) !important;
    background-color: var(--x-back-1) !important;
    color: initial;
  }
}
/* icon+文案宽度 1000px，超出显示省略号；icon与文字垂直居中，与 Figma 11454-17531 一致 */
.ppt-create__upload-progress {
  color: var(--x-back-10);
  max-width: 1000px;
  overflow: hidden;
}
.ppt-create__upload-progress-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.ppt-create__upload-progress-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ppt-create__upload-progress-name-wrap {
  flex: 1;
  min-width: 0;
  overflow: hidden;

  :deep(.tooltip-trigger) {
    min-width: 0;
    overflow: hidden;
    display: block;
  }
}
.ppt-create__upload-progress-name {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.5;
  max-width: 986px;
}

.ppt-create__upload-progress-desc {
  color: var(--x-text-5);
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.ppt-create__upload-progress-icon-spin {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.ppt-create__upload-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;
}
.ppt-create__upload-empty-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.ppt-create__upload-empty-title-free {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--x-error-6);
  width: 54px;
  height: 18px;
  border-radius: var(--x-radius-small);
  color: var(--x-back-1);
  font-size: var(--x-text-12-size);
}
.ppt-create__upload-empty-icon {
  width: 40px;
  height: 40px;
}
</style>
<style lang="scss">
/* tooltip 水平居中于页面（teleport 到 body，需非 scoped） */
.ppt-create__tooltip--page-center {
  left: 50vw !important;
  transform: translateX(-50%) !important;
}
</style>
